// Brain data-access layer. Normalizes every response into a status the UI can
// degrade on. A backend *_ENABLED flag being OFF returns HTTP 404 — we map that
// to status "disabled" (renders the "capability not enabled" panel), NEVER an error.
import axios from "axios";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AX = { withCredentials: true };

// The capability matrix shown at the top of Brain. `endpoint` is a cheap probe.
// goals + state are NOT server-flag-gated (always live for a founder); the rest
// 404 when their *_ENABLED env is off.
export const CAPABILITIES = [
  { key: "queue", label: "Executive Queue", endpoint: "/executive/queue", flag: "EXECUTIVE_QUEUE_ENABLED", gated: true },
  { key: "goals", label: "Goals", endpoint: "/goals", flag: "(always on)", gated: false },
  { key: "state", label: "State / Gaps", endpoint: "/state/gaps", flag: "(always on)", gated: false },
  { key: "outcomes", label: "Outcome Spine", endpoint: "/outcomes/summary", flag: "OUTCOME_SPINE_ENABLED", gated: true },
  { key: "journal", label: "Executive Journal", endpoint: "/journal/summary", flag: "EXECUTIVE_JOURNAL_ENABLED", gated: true },
  { key: "lessons", label: "Institutional Learning", endpoint: "/lessons/summary", flag: "INSTITUTIONAL_LEARNING_ENABLED", gated: true },
];

// Returns { status: "ok"|"disabled"|"error", data, error, httpStatus }.
export async function fetchBrain(endpoint, params) {
  try {
    const res = await axios.get(`${API}${endpoint}`, { ...AX, params });
    return { status: "ok", data: res.data, httpStatus: res.status };
  } catch (err) {
    const httpStatus = err && err.response && err.response.status;
    if (httpStatus === 404) {
      // Capability dark-launched / disabled — an expected, non-error condition.
      return { status: "disabled", data: null, httpStatus };
    }
    return { status: "error", data: null, error: err, httpStatus };
  }
}

// The ONLY write the Brain performs: record an operator decision on a recommendation via
// the REAL POST /api/recommendations/{id}/decide. Callers MUST gate this behind an
// explicit founder confirmation (see regions/ChallengeAction) — never call it implicitly.
// decision ∈ ACCEPTED | OVERRIDDEN | IGNORED.
export async function decideRecommendation(recommendationId, { decision, reasoning = "", category = "" }) {
  const res = await axios.post(
    `${API}/recommendations/${encodeURIComponent(recommendationId)}/decide`,
    { decision, reasoning, category },
    AX
  );
  return res.data; // { status: "decided", recommendation_id, decision }
}

// Founder-assigned Skill retrieval access (POST). Kept beside `fetchBrain` and
// deliberately NOT folded into it: `fetchBrain` maps 404 to "disabled" because a
// dark read surface is not an error, and applying that to a write would turn a
// failed assignment into a silent no-op the UI reports as success.
//
// A write returns { ok: true, data } or { ok: false, status, detail }. `detail`
// is FastAPI's own message — the service layer's refusals (not a skill, already
// granted, wrong state) are written to be read by a person, so they are shown
// rather than replaced with a generic failure string.
async function writeBrain(path, body) {
  try {
    const res = await axios.post(`${API}${path}`, body, AX);
    return { ok: true, data: res.data };
  } catch (err) {
    const r = err && err.response;
    return {
      ok: false,
      status: r && r.status,
      detail: (r && r.data && r.data.detail) ||
        (r && r.status === 404
          ? "The skill-access surface is not enabled in this environment."
          : "The request failed. Nothing has been changed."),
    };
  }
}

// Assign permanent retrieval access for one (agent, skill) pair. This creates a
// RETRIEVAL permission and never a capability grant — a Skill is not grantable.
export function assignSkill({ agentId, capabilityId, reason = "" }) {
  return writeBrain("/brain/skill-access/assignments", {
    agent_id: agentId, capability_id: capabilityId, reason,
  });
}

// Withdraw one. The row is marked revoked, not deleted, so the audit trail of
// who held what survives the withdrawal.
export function revokeSkillAssignment(requestId, { reason = "" } = {}) {
  return writeBrain(
    `/brain/skill-access/assignments/${encodeURIComponent(requestId)}/revoke`,
    { reason }
  );
}
